import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { CompanyDetailsFooter } from '@/components/CompanyDetailsFooter';
import { S3_BUCKET_URL } from '@/constants';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSquare,
  OptionItemSquareList,
} from '@/pages/Onboarding/components/OptionItemSquare';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { BODY_SHAPE_OPTIONS as BODY_SHAPE_OPTIONS_FEMALE } from '@/pages/Onboarding/options/BodyShape/BODY_SHAPE_OPTIONS';
import { HAIR_COLOR_OPTIONS } from '@/pages/Onboarding/options/HairColor/HAIR_COLOR_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { ScreenBodyShapeStyles } from './BodyShape.styles';

const BODY_SHAPE_OPTIONS_MALE: OnbOptionType<LayoutDataType['body_shape']>[] = [
  { id: 'slim', title: 'Slim' },
  { id: 'tone', title: 'Tone' },
  { id: 'muscular', title: 'Muscular' },
  { id: 'plus_size', title: 'Plus Size' },
];

const getOptionImage = (
  optionId: string,
  gender: LayoutDataType['gender'],
): string => {
  if (gender === 'male') {
    return `${S3_BUCKET_URL}/layout-own-ai-man/body_shape/${optionId}.png`;
  }
  return `${S3_BUCKET_URL}/layout-own-ai/body_shape/${optionId}.png`;
};

export const ScreenBodyShape: React.FC = () => {
  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const body_shape = form.watch('body_shape');
  const gender = form.watch('gender');

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'BodyShape',
      });
    }
  }, []);

  React.useEffect(() => {
    if (gender === 'female') {
      HAIR_COLOR_OPTIONS.forEach((option) => {
        preloadImage(
          `${S3_BUCKET_URL}/layout-own-ai/hair_color/${option.id}.png`,
        );
      });
    }
  }, [gender]);

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['body_shape']>,
  ) => {
    form.setValue('body_shape', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        body_shape: option.title,
      },
    });
    if (gender === 'male') {
      nextStepTimeout(() => wizard.goToStep('about_look'));
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = (): void => {
    wizard.goToStep('ethnicity');
  };

  const options =
    gender === 'male' ? BODY_SHAPE_OPTIONS_MALE : BODY_SHAPE_OPTIONS_FEMALE;
  const title = gender === 'male' ? 'Choose his' : 'Choose her';

  return (
    <ScreenBodyShapeStyles>
      <OnboardingNavigation onBack={handleBackStep} />

      <OwnAiTitle>
        {title} <OwnAiTitleHighlight>body shape</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSquareList>
          {options.map((option) => (
            <OptionItemSquare
              key={option.id}
              isSelected={body_shape === option.id}
              onClick={() => handleSelect(option)}
              bgSrc={getOptionImage(option.id, gender ?? 'female')}
              title={option.title}
              TEST_ID={`body_shape-${option.id}`}
            />
          ))}
        </OptionItemSquareList>
      </IonContent>
      <CompanyDetailsFooter extraPadding />
    </ScreenBodyShapeStyles>
  );
};
